import { FC, useContext, useEffect, useState } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import AutoSizer from 'react-virtualized-auto-sizer'
import { DragHandle as DragHandleIcon } from '@mui/icons-material'
import { Box, Grid, SxProps } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { useSubscribeLineDragHandlerMouseOver } from '@pages/FormCreateOrEdit/hooks/useSubscribeLineDragHandlerMouseOver'

import { FORM_DROPPABLE_TYPE } from '@constants'

import { EVENT_NAME, publish } from '../../../../events'

import { DraggableFieldOnSection } from './DraggableFieldOnSection'
import { WindowConfigContext } from './WindowConfig'

const lineSx: SxProps = {
  px: 1,
  pr: 5,
  mb: 1,
  height: 56,
}

type DraggableLineOnSectionProps = {
  containerId: number | string | null | undefined
  tabId: number | string | null | undefined
  index: number
  line: WindowConfigLineType
}

export const DraggableLineOnSection: FC<DraggableLineOnSectionProps> = ({
  line,
  tabId,
  index,
  containerId,
}) => {
  const { currentDragged } = useContext(WindowConfigContext)
  const lineId = line.id
  const pathToLine = `containerId:${containerId}_tabId:${tabId}_lineId:${lineId}`
  const [countSizeRow, setCountSizeRow] = useState(line.elements.reduce((a, b) => a + b.size, 0))

  useEffect(() => {
    if (line) {
      setCountSizeRow(line.elements.reduce((a, b) => a + b.size, 0))
    }
  }, [line])

  const isEmptyLine = line.elements.length === 0

  const { renderEmptyLine } = useSubscribeLineDragHandlerMouseOver({
    currentDragged,
    isEmptyLine,
  })

  const handleMouseEnter = () => {
    publish(EVENT_NAME.CONFIGURED_FORM_ELEMENTS_MOUSE_ENTER)
  }
  const handleMouseLeave = () => {
    publish(EVENT_NAME.CONFIGURED_FORM_ELEMENTS_MOUSE_LEAVE)
  }

  // Если курсор над drag handler линии, рендерим обычный Box, а не Draggable
  // Так как нельзя допускать возможности изменения порядка дефолтной пустой линии при изменении порядка остальных линий
  if (isEmptyLine && !renderEmptyLine) {
    return <Box key={line.id} sx={lineSx} onMouseLeave={handleMouseLeave} />
  }

  return (
    <Draggable draggableId={pathToLine} index={index} isDragDisabled={isEmptyLine}>
      {provided => (
        <Box ref={provided.innerRef} {...provided.draggableProps} sx={lineSx}>
          <Grid container alignItems={'baseline'}>
            <Grid
              item
              {...provided.dragHandleProps}
              sx={{ mr: 1, opacity: isEmptyLine ? 0 : 1 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <DragHandleIcon color={'action'} />
            </Grid>
            <Droppable
              ignoreContainerClipping
              direction='horizontal'
              droppableId={pathToLine}
              type={FORM_DROPPABLE_TYPE.DEFAULT}
            >
              {(provided, snapshot) => {
                return (
                  <AutoSizer>
                    {({ width }) => (
                      <Grid
                        ref={provided.innerRef}
                        container
                        {...provided.droppableProps}
                        flexWrap={'nowrap'}
                        sx={{
                          width,
                          display: 'flex',
                          alignItems: 'center',
                          ...(snapshot.isDraggingOver && {
                            background: theme => alpha(theme.palette.grey[600], 0.1),
                          }),
                        }}
                      >
                        {line?.elements.map((element, elementIndex) => (
                          <DraggableFieldOnSection
                            key={element.id}
                            countSizeRow={countSizeRow}
                            element={element}
                            index={elementIndex}
                            pathToLine={pathToLine}
                            width={width}
                          />
                        ))}
                        {provided.placeholder}
                      </Grid>
                    )}
                  </AutoSizer>
                )
              }}
            </Droppable>
          </Grid>
        </Box>
      )}
    </Draggable>
  )
}
